import React, { useEffect, useState } from 'react';
import { Car, Calculator, DollarSign, Percent } from 'lucide-react';
import Card from '../../components/UI/Card';
import CalculatorInput from '../../components/Tools/CalculatorInput';
import useTranslation from '../../hooks/useTranslation';

const MAKES = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler',
  'Dodge', 'FIAT', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI',
  'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Mazda',
  'Mercedes-Benz', 'MINI', 'Nissan', 'Porsche', 'Ram', 'Toyota', 'Volkswagen', 'Volvo',
];

const MSD_CAPABLE_BRANDS = ['Audi', 'BMW', 'INFINITI', 'Lexus', 'Mercedes-Benz', 'MINI', 'Mazda', 'Nissan', 'Toyota', 'Volvo'];

const DEAL_SCORE_LABELS = {
  'lease.deal.great': 'Great Deal',
  'lease.deal.average': 'Average Deal',
  'lease.deal.bad': 'Bad Deal',
};

const DEAL_SCORE_MESSAGES = {
  'lease.deal.msg.great': 'This is a great lease deal (low APR and low effective monthly).',
  'lease.deal.msg.average': 'This lease structure looks typical for current market terms.',
  'lease.deal.msg.bad': 'This deal is worse than average (high APR or high monthly cost).',
};

const FINANCE_SCORE_LABELS = {
  'lease.finance.great': 'Great Financing',
  'lease.finance.average': 'Typical Financing',
  'lease.finance.bad': 'High-Cost Financing',
};

const FINANCE_SCORE_MESSAGES = {
  'lease.finance.msg.great': 'Low APR and a strong monthly payment compared to the price—excellent financing terms.',
  'lease.finance.msg.average': 'This financing offer tracks with standard market rates.',
  'lease.finance.msg.bad': 'APR or total interest is high for this price—consider negotiating or alternative financing.',
};

const initialResults = {
  monthlyPayment: 0,
  totalLeaseCost: 0,
  rentCharge: 0,
  monthlyTaxes: 0,
  upfrontCosts: 0,
  dueAtSigning: 0,
  firstPayment: 0,
  userDownPayment: 0,
  upfrontFees: 0,
  upfrontSalesTax: 0,
  financePayment: 0,
  financeTotalCost: 0,
  financeDownPayment: 0,
  financeDueAtSigning: 0,
  financeDealScoreKey: '',
  financeDealColor: '',
  financeDealScore: '',
  financeDealMsgKey: '',
  financeDealMsg: '',
  dealScoreKey: '',
  dealColor: '',
  dealScore: '',
  dealMsgKey: '',
  dealMsg: '',
};

const LeaseAnalyzer = () => {
  const { t } = useTranslation();

  const [inputs, setInputs] = useState({
    msrp: '',
    sellingPrice: '',
    residualPercent: '',
    residualValue: '',
    moneyFactor: '',
    leaseTerm: '36',
    annualMiles: '12000',
    downPayment: '',
    tradeInValue: '',
    rebates: '',
    acquisitionFee: '',
    dispositionFee: '',
    dealerFee: '',
    govFee: '',
    salesTax: '',
    upfrontFees: '',
    upfrontSalesTax: '',
    make: MAKES[0] || '',
  });

  const [results, setResults] = useState(initialResults);
  const [activeTab, setActiveTab] = useState('lease');
  const [msd, setMsd] = useState({ enabled: false, deposits: 1 });

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'msrp' || field === 'residualPercent') {
      const msrp = parseFloat(field === 'msrp' ? value : inputs.msrp) || 0;
      const residualPercent = parseFloat(field === 'residualPercent' ? value : inputs.residualPercent) || 0;

      if (msrp > 0 && residualPercent > 0) {
        setInputs((prev) => ({
          ...prev,
          residualValue: (msrp * (residualPercent / 100)).toFixed(0),
        }));
      }
    }
  };

  const analyzeLease = () => {
    const msrp = parseFloat(inputs.msrp) || 0;
    const sellingPrice = parseFloat(inputs.sellingPrice) || msrp;
    const residualValue = parseFloat(inputs.residualValue) || (msrp * (parseFloat(inputs.residualPercent) / 100));
    const moneyFactor = parseFloat(inputs.moneyFactor) || 0;
    const parsedDownPayment = parseFloat(inputs.downPayment);
    const hasDownPayment = !Number.isNaN(parsedDownPayment);
    const downPaymentMagnitude = hasDownPayment ? Math.abs(parsedDownPayment) : 0;
    const downPayment = hasDownPayment
      ? parsedDownPayment >= 0
        ? -downPaymentMagnitude
        : downPaymentMagnitude
      : 0;
    const tradeIn = parseFloat(inputs.tradeInValue) || 0;
    const rebates = parseFloat(inputs.rebates) || 0;
    const acquisitionFee = parseFloat(inputs.acquisitionFee) || 0;
    const dealerFee = parseFloat(inputs.dealerFee) || 0;
    const govFee = parseFloat(inputs.govFee) || 0;
    const salesTax = parseFloat(inputs.salesTax) || 0;
    const upfrontFees = parseFloat(inputs.upfrontFees) || 0;
    const upfrontSalesTax = parseFloat(inputs.upfrontSalesTax) || 0;
    const leaseTerm = parseInt(inputs.leaseTerm, 10) || 36;
    const dispositionFee = parseFloat(inputs.dispositionFee) || 0;

    const hasLeaseInputs = msrp > 0 && sellingPrice > 0 && residualValue > 0 && moneyFactor > 0;
    if (!hasLeaseInputs) {
      const baselineDue = Math.max((downPayment || 0) + upfrontFees + upfrontSalesTax, 0);
      const baselineUpfrontCosts = baselineDue + Math.max(dispositionFee, 0);
      const financeDownPayment = downPayment + tradeIn + rebates;

      setResults({
        ...initialResults,
        upfrontCosts: baselineUpfrontCosts,
        dueAtSigning: baselineDue,
        financeDownPayment,
        financeDueAtSigning: baselineDue,
        userDownPayment: downPayment,
        upfrontFees,
        upfrontSalesTax,
      });
      return;
    }

    const adjustedCapCost = sellingPrice - downPayment - tradeIn - rebates + acquisitionFee + dealerFee + govFee;
    const depreciation = (adjustedCapCost - residualValue) / leaseTerm;
    let adjustedMF = moneyFactor;
    if (msd.enabled && MSD_CAPABLE_BRANDS.includes(inputs.make)) {
      adjustedMF = Math.max(moneyFactor - (msd.deposits * 0.00007), moneyFactor * 0.3);
    }
    const rentCharge = (adjustedCapCost + residualValue) * adjustedMF;
    const basePayment = depreciation + rentCharge;
    const monthlyTaxes = basePayment * (salesTax / 100);
    const monthlyPayment = basePayment + monthlyTaxes;
    const firstPayment = monthlyPayment;
    const msdAmount = msd.enabled ? monthlyPayment * msd.deposits : 0;
    const dueAtSigning = firstPayment + (downPayment || 0) + upfrontFees + upfrontSalesTax;
    const upfrontCosts = dueAtSigning + msdAmount + dispositionFee;
    const totalLeaseCost = (monthlyPayment * leaseTerm) + upfrontCosts - msdAmount;

    const financeTerms = 60;
    const apr = (moneyFactor * 2400) / 100 || 0.05;
    const monthlyRate = apr / 12;
    const downPaymentApplied = hasDownPayment ? downPaymentMagnitude : 0;
    const financeAmount = sellingPrice - downPaymentApplied - tradeIn - rebates;
    let financePayment = 0;
    if (financeAmount > 0 && monthlyRate > 0) {
      const pow = Math.pow(1 + monthlyRate, financeTerms);
      financePayment = financeAmount * ((monthlyRate * pow) / (pow - 1));
    } else if (financeAmount > 0) {
      financePayment = financeAmount / financeTerms;
    }
    const financeTotalCost = financePayment * financeTerms;
    const financeInterestPaid = Math.max(financeTotalCost - Math.max(financeAmount, 0), 0);
    const financeDueAtSigning = monthlyPayment + (downPayment || 0) + upfrontFees + upfrontSalesTax;
    const financeDownPayment = downPayment + tradeIn + rebates;

    const financeEffectiveMonthly = financeTotalCost / financeTerms || 0;
    const financeAprPercent = (apr || 0) * 100;
    let financeDealScoreKey = 'lease.finance.average';
    let financeDealColor = 'bg-yellow-100 text-yellow-800';
    let financeDealMsgKey = 'lease.finance.msg.average';

    if (financeAprPercent <= 4 && financeInterestPaid <= Math.max(financeAmount, 0) * 0.15 && financeEffectiveMonthly <= (msrp || sellingPrice) / financeTerms * 1.05) {
      financeDealScoreKey = 'lease.finance.great';
      financeDealColor = 'bg-green-100 text-green-800';
      financeDealMsgKey = 'lease.finance.msg.great';
    } else if (financeAprPercent >= 7 || financeInterestPaid > Math.max(financeAmount, 0) * 0.3) {
      financeDealScoreKey = 'lease.finance.bad';
      financeDealColor = 'bg-red-100 text-red-800';
      financeDealMsgKey = 'lease.finance.msg.bad';
    }

    const effectiveMonthly = totalLeaseCost / leaseTerm;
    let dealScoreKey = 'lease.deal.average';
    let dealColor = 'bg-yellow-100 text-yellow-800';
    let dealMsgKey = 'lease.deal.msg.average';

    if (moneyFactor * 2400 < 5 && msrp && effectiveMonthly < msrp * 0.01) {
      dealScoreKey = 'lease.deal.great';
      dealColor = 'bg-green-100 text-green-800';
      dealMsgKey = 'lease.deal.msg.great';
    } else if (moneyFactor * 2400 > 7 || effectiveMonthly > msrp * 0.012) {
      dealScoreKey = 'lease.deal.bad';
      dealColor = 'bg-red-100 text-red-800';
      dealMsgKey = 'lease.deal.msg.bad';
    }

    setResults({
      monthlyPayment,
      totalLeaseCost,
      rentCharge,
      monthlyTaxes,
      upfrontCosts,
      dueAtSigning,
      firstPayment,
      userDownPayment: downPayment,
      upfrontFees,
      upfrontSalesTax,
      financePayment,
      financeTotalCost,
      financeDownPayment,
      financeDueAtSigning,
      financeDealScoreKey,
      financeDealScore: FINANCE_SCORE_LABELS[financeDealScoreKey],
      financeDealColor,
      financeDealMsgKey,
      financeDealMsg: FINANCE_SCORE_MESSAGES[financeDealMsgKey],
      dealScoreKey,
      dealScore: DEAL_SCORE_LABELS[dealScoreKey],
      dealColor,
      dealMsgKey,
      dealMsg: DEAL_SCORE_MESSAGES[dealMsgKey],
    });
  };

  useEffect(() => {
    analyzeLease();
    // eslint-disable-next-line
  }, [inputs, msd]);

  const formatCurrency = (value) => {
    const numericValue = Number(value);
    const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(safeValue);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary-600" />
          {t('lease.title', 'Lease & Finance Analyzer')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('lease.description', 'Analyze your car lease or finance deal, compare costs, and see the impact of MSDs and incentives.')}
        </p>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Car className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('lease.section.vehicle', 'Vehicle Information')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t('lease.input.make', 'Make')}
                </label>
                <select
                  className="w-full rounded border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:bg-dark-900 dark:text-white p-2 bg-white"
                  value={inputs.make}
                  onChange={(event) => handleInputChange('make', event.target.value)}
                >
                  {MAKES.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </div>
              <CalculatorInput
                label={t('lease.input.msrp', 'MSRP (Sticker Price) *')}
                value={inputs.msrp}
                onChange={(value) => handleInputChange('msrp', value)}
                placeholder="35000"
                helperText={t('lease.helper.msrp', "Manufacturer's suggested retail price")}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.sellingPrice', 'Negotiated Price *')}
                value={inputs.sellingPrice}
                onChange={(value) => handleInputChange('sellingPrice', value)}
                placeholder="32000"
                helperText={t('lease.helper.sellingPrice', 'Your negotiated selling price')}
                prefix="$"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t('lease.input.term', 'Lease Term (months) *')}
                </label>
                <select
                  className="w-full rounded border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:bg-dark-900 dark:text-white p-2 bg-white"
                  value={inputs.leaseTerm}
                  onChange={(event) => handleInputChange('leaseTerm', event.target.value)}
                >
                  {[24, 36, 39, 42, 48, 60].map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  {t('lease.input.miles', 'Annual Miles Allowed *')}
                </label>
                <select
                  className="w-full rounded border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 dark:bg-dark-900 dark:text-white p-2 bg-white"
                  value={inputs.annualMiles}
                  onChange={(event) => handleInputChange('annualMiles', event.target.value)}
                >
                  {[10000, 12000, 15000, 18000].map((miles) => (
                    <option key={miles} value={miles}>
                      {miles.toLocaleString()} {t('lease.input.milesSuffix', 'miles')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Percent className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('lease.section.terms', 'Lease Terms')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('lease.input.residualPercent', 'Residual Value (%)')}
                value={inputs.residualPercent}
                onChange={(value) => handleInputChange('residualPercent', value)}
                placeholder="56.5"
                helperText={t('lease.helper.residualPercent', 'Residual as percent of MSRP')}
              />
              <CalculatorInput
                label={t('lease.input.residualValue', 'Residual Value ($)')}
                value={inputs.residualValue}
                onChange={(value) => handleInputChange('residualValue', value)}
                placeholder="21000"
                helperText={t('lease.helper.residualValue', 'Vehicle value at lease end')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.moneyFactor', 'Money Factor *')}
                value={inputs.moneyFactor}
                onChange={(value) => handleInputChange('moneyFactor', value)}
                placeholder="0.00125"
                helperText={t('lease.helper.moneyFactor', 'Interest rate divided by 2400')}
              />
              <CalculatorInput
                label={t('lease.input.salesTaxRate', 'Sales Tax Rate (%)')}
                value={inputs.salesTax}
                onChange={(value) => handleInputChange('salesTax', value)}
                placeholder="6.5"
                helperText={t('lease.helper.salesTaxRate', 'Your local sales tax rate')}
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('lease.section.financial', 'Financial Details')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('lease.input.downPayment', 'Down Payment')}
                value={inputs.downPayment}
                onChange={(value) => handleInputChange('downPayment', value)}
                placeholder="2500"
                helperText={t('lease.helper.downPayment', 'Enter a positive number for cap cost reduction or negative for rebates')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.tradeIn', 'Trade-In Value')}
                value={inputs.tradeInValue}
                onChange={(value) => handleInputChange('tradeInValue', value)}
                placeholder="1500"
                helperText={t('lease.helper.tradeIn', 'Your trade-in value (if any)')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.rebates', 'Incentives / Rebates')}
                value={inputs.rebates}
                onChange={(value) => handleInputChange('rebates', value)}
                placeholder="1500"
                helperText={t('lease.helper.rebates', 'Manufacturer or dealer incentives')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.acquisitionFee', 'Acquisition Fee')}
                value={inputs.acquisitionFee}
                onChange={(value) => handleInputChange('acquisitionFee', value)}
                placeholder="795"
                helperText={t('lease.helper.acquisitionFee', 'Bank or leasing company fee')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.dealerFee', 'Dealer Fee')}
                value={inputs.dealerFee}
                onChange={(value) => handleInputChange('dealerFee', value)}
                placeholder="499"
                helperText={t('lease.helper.dealerFee', 'Dealer documentation fee')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.govFee', 'Government Fees')}
                value={inputs.govFee}
                onChange={(value) => handleInputChange('govFee', value)}
                placeholder="350"
                helperText={t('lease.helper.govFee', 'DMV, registration, etc.')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.dispositionFee', 'Disposition Fee')}
                value={inputs.dispositionFee}
                onChange={(value) => handleInputChange('dispositionFee', value)}
                placeholder="350"
                helperText={t('lease.helper.dispositionFee', 'Fee at lease end (if any)')}
                prefix="$"
              />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('lease.section.due', 'Due at Signing Details')}
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CalculatorInput
                label={t('lease.input.upfrontFees', 'Up-Front Fees (DMV, Reg, etc.)')}
                value={inputs.upfrontFees}
                onChange={(value) => handleInputChange('upfrontFees', value)}
                placeholder="0"
                helperText={t('lease.helper.upfrontFees', 'Registration, doc, and other up-front fees')}
                prefix="$"
              />
              <CalculatorInput
                label={t('lease.input.upfrontSalesTax', 'Up-Front Sales Tax')}
                value={inputs.upfrontSalesTax}
                onChange={(value) => handleInputChange('upfrontSalesTax', value)}
                placeholder="0"
                helperText={t('lease.helper.upfrontSalesTax', 'Sales tax paid at signing (if any)')}
                prefix="$"
              />
            </div>
          </Card>

          {MSD_CAPABLE_BRANDS.includes(inputs.make) && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('lease.section.msd', 'Multiple Security Deposits (MSD)')}
                </h3>
              </div>
              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={msd.enabled}
                    onChange={(event) => setMsd((prev) => ({ ...prev, enabled: event.target.checked }))}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {t('lease.section.msd.enable', 'Enable Multiple Security Deposits')}
                  </span>
                </label>
                {msd.enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('lease.section.msd.deposits', 'Number of Deposits (1-9)')}
                    </label>
                    <select
                      value={msd.deposits}
                      onChange={(event) => setMsd((prev) => ({ ...prev, deposits: parseInt(event.target.value, 10) }))}
                      className="w-full rounded border-gray-300 dark:bg-dark-900 dark:text-white p-2"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 lg:sticky lg:top-20 lg:self-start">
          <Card className="p-0 overflow-hidden">
            <div className="flex sticky top-0 z-10 bg-white dark:bg-dark-900 border-b border-gray-100 dark:border-dark-700">
              <button
                onClick={() => setActiveTab('lease')}
                className={`flex-1 px-3 py-2 text-[11px] sm:text-xs font-medium uppercase tracking-wide leading-tight whitespace-normal ${
                  activeTab === 'lease'
                    ? 'bg-primary-100 text-primary-900'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-dark-600'
                }`}
              >
                {t('lease.tab.lease', 'Lease')}
              </button>
              <button
                onClick={() => setActiveTab('finance')}
                className={`flex-1 px-3 py-2 text-[11px] sm:text-xs font-medium uppercase tracking-wide leading-tight whitespace-normal ${
                  activeTab === 'finance'
                    ? 'bg-primary-100 text-primary-900'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-dark-600'
                }`}
              >
                {t('lease.tab.finance', 'Finance')}
              </button>
              <button
                onClick={() => setActiveTab('comparison')}
                className={`flex-1 px-3 py-2 text-[11px] sm:text-xs font-medium uppercase tracking-wide leading-tight whitespace-normal ${
                  activeTab === 'comparison'
                    ? 'bg-primary-100 text-primary-900'
                    : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-dark-600'
                }`}
              >
                {t('lease.tab.comparison', 'Lease vs. Finance')}
              </button>
            </div>
            <div className="p-6">
              {activeTab === 'lease' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 uppercase tracking-wide">
                      {t('lease.summary.header.lease', 'Lease')}
                    </h3>
                  </div>
                  {results.dealScoreKey && (
                    <>
                      <div className={`rounded px-3 py-2 text-center font-semibold text-sm ${results.dealColor}`}>
                        {t(results.dealScoreKey, results.dealScore)}
                      </div>
                      <div className="text-xs text-gray-500 text-center mb-2">
                        {t(results.dealMsgKey, results.dealMsg)}
                      </div>
                    </>
                  )}
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.monthlyPayment', 'Monthly Payment:')}
                    </div>
                    <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                      {formatCurrency(results.monthlyPayment)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.dueAtSigning', 'Due at Signing:')}
                    </div>
                    <div className="bg-primary-100 text-primary-900 text-xl font-bold px-4 py-2 rounded text-center mb-2">
                      {formatCurrency(results.dueAtSigning)}
                    </div>
                    <div className="text-xs text-gray-500">
                      <div>
                        {t('lease.summary.firstPayment', 'First Payment:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.firstPayment)}</span>
                      </div>
                      <div>
                        {t('lease.summary.downPayment', 'Down Payment:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.userDownPayment)}</span>
                      </div>
                      <div>
                        {t('lease.summary.upfrontFees', 'Up-Front Fees:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.upfrontFees)}</span>
                      </div>
                      <div>
                        {t('lease.summary.upfrontSalesTax', 'Up-Front Sales Tax:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.upfrontSalesTax)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.totalCost', 'Total Cost:')}
                    </div>
                    <div className="text-2xl font-bold text-center">
                      {formatCurrency(results.totalLeaseCost)}
                    </div>
                    <div className="text-xs text-gray-400 text-center">
                      {t('lease.summary.overMonths', 'over {{months}} months', {
                        months: inputs.leaseTerm || 0,
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.effectiveMonthly', 'Effective Monthly Cost:')}
                    </div>
                    <div className="text-2xl font-bold text-center">
                      {formatCurrency(results.totalLeaseCost / (parseInt(inputs.leaseTerm, 10) || 1))}
                    </div>
                    <div className="text-xs text-gray-400 text-center">
                      {t('lease.summary.overMonths', 'over {{months}} months', {
                        months: inputs.leaseTerm || 0,
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="space-y-4">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 uppercase tracking-wide">
                      {t('lease.summary.header.finance', 'Finance')}
                    </h3>
                  </div>
                  {results.financeDealScoreKey && (
                    <>
                      <div className={`rounded px-3 py-2 text-center font-semibold text-sm ${results.financeDealColor}`}>
                        {t(results.financeDealScoreKey, results.financeDealScore)}
                      </div>
                      <div className="text-xs text-gray-500 text-center mb-2">
                        {t(results.financeDealMsgKey, results.financeDealMsg)}
                      </div>
                    </>
                  )}
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.financeMonthly', 'Monthly Payment:')}
                    </div>
                    <div className="bg-primary-100 text-primary-900 text-2xl font-bold px-4 py-2 rounded text-center">
                      {formatCurrency(results.financePayment)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.financeDueAtSigning', 'Due at Signing:')}
                    </div>
                    <div className="bg-primary-100 text-primary-900 text-xl font-bold px-4 py-2 rounded text-center">
                      {formatCurrency(results.financeDueAtSigning)}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 space-y-1">
                      <div>
                        {t('lease.summary.firstPayment', 'First Payment:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.financePayment)}</span>
                      </div>
                      <div>
                        {t('lease.summary.downPayment', 'Down Payment:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.financeDownPayment)}</span>
                      </div>
                      <div>
                        {t('lease.summary.upfrontFees', 'Up-Front Fees:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.upfrontFees)}</span>
                      </div>
                      <div>
                        {t('lease.summary.upfrontSalesTax', 'Up-Front Sales Tax:')}{' '}
                        <span className="font-semibold">{formatCurrency(results.upfrontSalesTax)}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.financeTotalCost', 'Total Cost:')}
                    </div>
                    <div className="text-2xl font-bold text-center">
                      {formatCurrency(results.financeTotalCost)}
                    </div>
                    <div className="text-xs text-gray-400 text-center">
                      {t('lease.summary.overFinanceMonths', 'over {{months}} months', { months: 60 })}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-2">
                      {t('lease.summary.financeEffectiveMonthly', 'Effective Monthly Cost:')}
                    </div>
                    <div className="text-2xl font-bold text-center">
                      {formatCurrency(results.financePayment)}
                    </div>
                    <div className="text-xs text-gray-400 text-center">
                      {t('lease.summary.overFinanceMonths', 'over {{months}} months', { months: 60 })}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'comparison' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="bg-gray-200 dark:bg-dark-700 px-3 py-1 rounded text-sm uppercase tracking-wide">
                      {t('lease.summary.header.lease', 'Lease')}
                    </div>
                    <div className="bg-gray-200 dark:bg-dark-700 px-3 py-1 rounded text-sm uppercase tracking-wide">
                      {t('lease.summary.header.finance', 'Finance')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-3">
                      {t('lease.comparison.monthlyPayment', 'Monthly Payment:')}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="bg-primary-100 text-primary-900 text-xl font-bold px-3 py-2 rounded">
                        {formatCurrency(results.monthlyPayment)}
                      </div>
                      <div className="bg-primary-100 text-primary-900 text-xl font-bold px-3 py-2 rounded">
                        {formatCurrency(results.financePayment)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-3">
                      {t('lease.comparison.dueAtSigning', 'Due at Signing:')}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="bg-primary-100 text-primary-900 text-lg font-bold px-3 py-2 rounded">
                        {formatCurrency(results.upfrontCosts)}
                      </div>
                      <div className="bg-primary-100 text-primary-900 text-lg font-bold px-3 py-2 rounded">
                        {formatCurrency(results.financeDueAtSigning)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-3">
                      {t('lease.comparison.totalCost', 'Total Cost:')}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-center">
                        <div className="text-xl font-bold">{formatCurrency(results.totalLeaseCost)}</div>
                        <div className="text-xs text-gray-400">
                          {t('lease.summary.overMonths', 'over {{months}} months', {
                            months: inputs.leaseTerm || 0,
                          })}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold">{formatCurrency(results.financeTotalCost)}</div>
                        <div className="text-xs text-gray-400">
                          {t('lease.summary.overFinanceMonths', 'over {{months}} months', { months: 60 })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm uppercase tracking-wide text-gray-500 mb-3">
                      {t('lease.comparison.effectiveMonthly', 'Effective Monthly Cost:')}
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-center">
                        <div className="text-xl font-bold">
                          {formatCurrency(results.totalLeaseCost / (parseInt(inputs.leaseTerm, 10) || 1))}
                        </div>
                        <div className="text-xs text-gray-400">
                          {t('lease.summary.overMonths', 'over {{months}} months', {
                            months: inputs.leaseTerm || 0,
                          })}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold">{formatCurrency(results.financePayment)}</div>
                        <div className="text-xs text-gray-400">
                          {t('lease.summary.overFinanceMonths', 'over {{months}} months', { months: 60 })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-gray-100 dark:bg-dark-800 p-4">
              <button className="w-full bg-primary-100 text-primary-900 font-semibold py-2 px-4 rounded uppercase tracking-wide text-sm hover:bg-primary-200 transition-colors">
                {t('lease.summary.share', 'Share Your Numbers')}
              </button>
              <p className="text-xs text-gray-400 text-center mt-2">
                {t('lease.summary.shareHint', 'Click to generate a shareable link of your Lease vs. Finance comparison.')}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LeaseAnalyzer;
